import React, { useCallback, useEffect, useRef, useState } from 'react';
import UaePassIdleState from './UaePassIdleState';
import UaePassWaitingState from './UaePassWaitingState';
import UaePassSuccessState from './UaePassSuccessState';
import UaePassErrorState from './UaePassErrorState';
import {
    FlowUAEPassStatus,
    UaePassError,
    UaePassWindowCloseReason,
} from 'flow/types/UaePassLogin';
import {
    uaePassErrors,
    uaePassLoginStatus,
    uaePassSignInResult,
    uaeWindowCloseReasons,
} from 'flow/constants/uae-pass/uae-pass-constants';
import { useUAEPass } from 'flow/hooks/useUAEPass';
import { mapSignInErrorToUaePassError } from 'flow/utils/uaepass-api-utils';
import useUaePassSuccessRedirect from 'flow/hooks/useUaePassSuccessRedirect';

interface Props {
    /* Lets the sign-in screen drop the form while a flow is running. */
    onFlowActiveChange?: (isActive: boolean) => void;
}

const UaePassSignIn: React.FC<Props> = ({ onFlowActiveChange }) => {
    const handleSuccessComplete = useUaePassSuccessRedirect();
    const [status, setStatus] = useState<FlowUAEPassStatus>(
        uaePassLoginStatus.IDLE,
    );
    const [errorCode, setErrorCode] = useState<UaePassError | null>(null);
    const prevStatusRef = useRef(status);
    const isIdle = status === uaePassLoginStatus.IDLE;

    useEffect(() => {
        onFlowActiveChange?.(!isIdle);
    }, [isIdle, onFlowActiveChange]);

    const handleWindowClosed = useCallback(
        (reason: UaePassWindowCloseReason) => {
            if (reason !== uaeWindowCloseReasons.REDIRECT) {
                setErrorCode(
                    (prevErrorCode) =>
                        prevErrorCode ?? uaePassErrors.USER_CANCELLED,
                );
                setStatus(uaePassLoginStatus.ERROR);
            }
        },
        [],
    );
    const {
        signIn,
        resetUaePass,
        uaePassError,
        clearUaePassError,
        closeUaePassWindow,
        finishSignInData,
        finishSignInError,
        signInError,
    } = useUAEPass({
        onWindowClosed: handleWindowClosed,
    });

    useEffect(() => {
        if (uaePassError) {
            setErrorCode(uaePassError);
            setStatus(uaePassLoginStatus.ERROR);
        }
    }, [uaePassError]);

    const handleStartAuth = useCallback(() => {
        if (status !== uaePassLoginStatus.IDLE) return;
        setStatus(uaePassLoginStatus.WAITING);
        signIn();
    }, [signIn, status]);

    const handleBackToLogin = useCallback(() => {
        resetUaePass();
        clearUaePassError();
        setErrorCode(null);
        setStatus(uaePassLoginStatus.IDLE);
    }, [resetUaePass, clearUaePassError]);

    const handleTimeout = useCallback(() => {
        setErrorCode(uaePassErrors.TIMEOUT);
        setStatus(uaePassLoginStatus.ERROR);
    }, []);

    useEffect(() => {
        if (
            prevStatusRef.current === uaePassLoginStatus.WAITING &&
            status !== uaePassLoginStatus.WAITING
        ) {
            closeUaePassWindow();
        }
        prevStatusRef.current = status;
    }, [status, closeUaePassWindow]);

    useEffect(() => {
        if (status === uaePassLoginStatus.WAITING) {
            if (signInError) {
                console.error('UAE Pass signIn failed:', signInError);
                setErrorCode(mapSignInErrorToUaePassError(signInError));
                setStatus(uaePassLoginStatus.ERROR);
            } else if (finishSignInError) {
                console.error(
                    'UAE Pass finishSignIn failed:',
                    finishSignInError,
                );
                setErrorCode(uaePassErrors.GENERIC);
                setStatus(uaePassLoginStatus.ERROR);
            }
        }
    }, [finishSignInError, status, signInError]);

    useEffect(() => {
        if (status === uaePassLoginStatus.WAITING && finishSignInData) {
            if (finishSignInData.signInStatus === uaePassSignInResult.OK) {
                setStatus(uaePassLoginStatus.SUCCESS);
            } else if (
                finishSignInData.signInStatus === uaePassSignInResult.ERROR
            ) {
                setErrorCode(uaePassErrors.GENERIC);
                setStatus(uaePassLoginStatus.ERROR);
            }
        }
    }, [finishSignInData, status]);

    /* Idle renders inline among the other sign-in options; the rest replace the form. */
    if (isIdle) {
        return <UaePassIdleState onSignIn={handleStartAuth} />;
    }

    const renderCurrentState = () => {
        switch (status) {
            case uaePassLoginStatus.WAITING:
                return <UaePassWaitingState onTimeout={handleTimeout} />;
            case uaePassLoginStatus.SUCCESS:
                return (
                    <UaePassSuccessState onComplete={handleSuccessComplete} />
                );
            case uaePassLoginStatus.ERROR:
                return (
                    <UaePassErrorState
                        errorCode={errorCode}
                        onBackToLogin={handleBackToLogin}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex w-full flex-col px-4">{renderCurrentState()}</div>
    );
};

export default UaePassSignIn;
